import { getToken } from "./getToken";

/**
 * Which link the token currently in sessionStorage was minted for. Lives in
 * sessionStorage alongside that token so the two are scoped to the same tab
 * and are replaced together on every exchange.
 */
const ACTIVE_LINK_SESSION = "activeLinkSession";

const EXPIRY_KEY_PREFIX = "token-expiration-time-";

/**
 * The expiry markers this module has written, so a later exchange can reap the
 * dead ones. Nothing else cleans up the QR/dott-link markers —
 * `clearTokenTimeChecker` only removes the `deviceId`-keyed variant — and
 * without this an office iPad accumulates one entry per patient, forever.
 *
 * Only *expired* markers are reaped, and only ones recorded here. localStorage
 * is shared across tabs while the binding above is not, so another tab can be
 * holding a live session for a marker this tab did not write; deleting it
 * would leave that tab unable to recognise its own session on reload.
 */
const LINK_SESSION_REGISTRY = "linkSessionKeys";

const readRegistry = (): string[] => {
  try {
    const stored = JSON.parse(localStorage.getItem(LINK_SESSION_REGISTRY));

    return Array.isArray(stored)
      ? stored.filter(
          (key) => typeof key === "string" && key.startsWith(EXPIRY_KEY_PREFIX)
        )
      : [];
  } catch {
    return [];
  }
};

/** Drops the markers we recorded that have since expired. */
const reapExpiredMarkers = (currentKey: string): string[] =>
  readRegistry().filter((key) => {
    if (key === currentKey) {
      return false;
    }

    const expiresAt = Number(localStorage.getItem(key));

    if (Number.isFinite(expiresAt) && Date.now() < expiresAt) {
      return true;
    }

    localStorage.removeItem(key);

    return false;
  });

/**
 * Key under which a successful code-ID login records when the session it just
 * minted expires. Written by `useCodeIDBasedLogin`, read back by
 * `hasValidSessionForLink` — keep both sides on this helper so they cannot
 * drift apart.
 */
export const linkExpiryKey = (
  tokenId: string,
  formAssignmentId: string
): string => `token-expiration-time-${tokenId}-${formAssignmentId}`;

/**
 * Records when the session just minted from this QR/dott link expires, so a
 * later mount can tell that the link has already been exchanged, and binds the
 * token now in sessionStorage to that link, and reaps the markers that have
 * since expired.
 */
export const rememberSessionForLink = (
  tokenId: string,
  formAssignmentId: string,
  expiresAt: number
): void => {
  const key = linkExpiryKey(tokenId, formAssignmentId);
  const stillLive = reapExpiredMarkers(key);

  localStorage.setItem(key, expiresAt.toString());
  localStorage.setItem(
    LINK_SESSION_REGISTRY,
    JSON.stringify([...stillLive, key])
  );
  sessionStorage.setItem(ACTIVE_LINK_SESSION, key);
};

/**
 * Forgets which link the held token belonged to. Part of ending a session, so
 * `removeTokenData` calls it — the durable expiry marker is deliberately left
 * in place, since it records when that link's session was due to expire.
 */
export const clearActiveLinkSession = (): void => {
  sessionStorage.removeItem(ACTIVE_LINK_SESSION);
};

/**
 * True when this device already holds a live session that was minted from
 * *this exact* QR/dott link.
 *
 * A FASTR/Linked-Device link is single-use: the backend rejects a second
 * `/oauth/token` exchange for it. The in-memory `useRef` guard on
 * `FillFormsHandlerPage` only survives within one mount, so a reload,
 * pull-to-refresh, iOS memory-reload or Back re-exchanged an already-consumed
 * link and showed a false "link has expired" on a perfectly valid assignment
 * (YAP-9868, Symptom B). This guard is persisted instead: the token lives in
 * sessionStorage and the expiry marker in localStorage, so both survive a
 * same-tab reload.
 *
 * Keyed on the link — a different link on the same shared office iPad has no
 * session of its own and still goes through the exchange. The held token must
 * also be the one minted for this link: on a shared iPad the next patient's
 * exchange replaces the token but an earlier patient's marker can still be
 * unexpired, and reusing it would load one patient's forms under another
 * patient's session.
 */
export const hasValidSessionForLink = (
  tokenId: string,
  formAssignmentId: string
): boolean => {
  if (!tokenId || !formAssignmentId || !getToken()) {
    return false;
  }

  const key = linkExpiryKey(tokenId, formAssignmentId);

  if (sessionStorage.getItem(ACTIVE_LINK_SESSION) !== key) {
    return false;
  }

  const expiresAt = Number(localStorage.getItem(key));

  return Number.isFinite(expiresAt) && Date.now() < expiresAt;
};
